/**
 * PropertyAdminOffersReceivedTab
 * -------------------------------
 * Selling-side mirror of the Offers tab: lists every offer received on
 * the signed-in agent's own properties, grouped by property, with
 * Accept / Reject actions on each 'sent' offer. Decisions email the buyer
 * best-effort via Resend on the backend.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Inbox, Check, X, Mail, Phone, Undo2, MessageCircle } from "lucide-react";
import WhatsAppUpdateModal from "./WhatsAppUpdateModal";

const API = getApiUrl();

function formatAmount(amount, currency = "£") {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  return `${currency}${n.toLocaleString(undefined, {
    maximumFractionDigits: n >= 1000 ? 0 : 2,
  })}`;
}

function StatusBadge({ status }) {
  const cls =
    status === "accepted"
      ? "bg-green-100 text-green-700"
      : status === "rejected"
      ? "bg-red-100 text-red-700"
      : "bg-amber-100 text-amber-700";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded ${cls} capitalize`}>
      {status || "sent"}
    </span>
  );
}

function OfferRow({ offer, onDecide, onRevoke, onMessageBuyer, busy }) {
  const decided = offer.status === "accepted" || offer.status === "rejected";
  return (
    <div
      className="border-t border-border px-3 py-3"
      data-testid={`received-offer-row-${offer.offer_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">
              {formatAmount(offer.amount, offer.currency || "£")}
            </span>
            <StatusBadge status={offer.status} />
            <span className="text-[11px] text-muted-foreground">
              {offer.created_at
                ? new Date(offer.created_at).toLocaleString()
                : ""}
            </span>
          </div>
          <div className="mt-1 text-xs text-foreground/80">
            <span className="font-medium">
              {offer.buyer_name || "Buyer"}
            </span>
            {offer.buyer_email && (
              <a
                href={`mailto:${offer.buyer_email}`}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
                data-testid={`received-offer-email-${offer.offer_id}`}
              >
                <Mail className="h-3 w-3" /> {offer.buyer_email}
              </a>
            )}
            {offer.buyer_phone && (
              <a
                href={`tel:${offer.buyer_phone}`}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <Phone className="h-3 w-3" /> {offer.buyer_phone}
              </a>
            )}
            {offer.buyer_phone && (
              <button
                type="button"
                onClick={() => onMessageBuyer(offer)}
                className="ml-2 inline-flex items-center gap-1 text-emerald-700 hover:underline"
                data-testid={`received-offer-whatsapp-${offer.offer_id}`}
              >
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </button>
            )}
          </div>
          {(offer.buying_position || offer.move_timeline || offer.position_notes) && (
            <div
              className="mt-2 border border-border/60 bg-white p-2 text-xs space-y-1"
              data-testid={`received-offer-position-${offer.offer_id}`}
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Buyer position
              </div>
              <div className="flex flex-wrap gap-2">
                {offer.buying_position && (
                  <span className="px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded font-medium">
                    {offer.buying_position}
                  </span>
                )}
                {offer.move_timeline && (
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 rounded">
                    Wants to move: {offer.move_timeline}
                  </span>
                )}
              </div>
              {offer.position_notes && (
                <div className="text-muted-foreground whitespace-pre-wrap pt-1 border-t border-border/40">
                  {offer.position_notes}
                </div>
              )}
            </div>
          )}
          {offer.message ? (
            <div className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap bg-muted/30 border-l-2 border-border pl-2 py-1">
              {offer.message}
            </div>
          ) : null}
          {decided && offer.decision_note ? (
            <div
              className="text-xs mt-2 whitespace-pre-wrap bg-slate-50 border-l-2 border-slate-400 pl-2 py-1"
              data-testid={`received-offer-decision-note-${offer.offer_id}`}
            >
              <span className="text-slate-500 font-medium">Your note: </span>
              {offer.decision_note}
            </div>
          ) : null}
          {decided && offer.decided_at ? (
            <div className="text-[11px] text-muted-foreground mt-2">
              {offer.status === "accepted" ? "Accepted" : "Rejected"} on{" "}
              {new Date(offer.decided_at).toLocaleString()}
            </div>
          ) : null}
          {offer.status === "withdrawn" && offer.withdrawn_at ? (
            <div className="text-[11px] text-muted-foreground mt-2">
              Buyer withdrew on {new Date(offer.withdrawn_at).toLocaleString()}
              {offer.withdraw_note ? (
                <div className="text-xs mt-1 whitespace-pre-wrap bg-slate-50 border-l-2 border-slate-400 pl-2 py-1">
                  <span className="text-slate-500 font-medium">Buyer&apos;s note: </span>
                  {offer.withdraw_note}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        {!decided && offer.status !== "withdrawn" && (
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              className="rounded-none h-7 text-xs"
              disabled={busy}
              onClick={() => onDecide(offer, "accepted")}
              data-testid={`received-offer-accept-${offer.offer_id}`}
            >
              <Check className="h-3 w-3 mr-1" /> Accept
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
              disabled={busy}
              onClick={() => onDecide(offer, "rejected")}
              data-testid={`received-offer-reject-${offer.offer_id}`}
            >
              <X className="h-3 w-3 mr-1" /> Reject
            </Button>
          </div>
        )}
        {decided && (
          <Button
            size="sm"
            variant="outline"
            className="rounded-none h-7 text-xs"
            disabled={busy}
            onClick={() => onRevoke(offer)}
            data-testid={`received-offer-revoke-${offer.offer_id}`}
          >
            <Undo2 className="h-3 w-3 mr-1" /> Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

function DecisionDialog({ open, onOpenChange, offer, decision, onConfirm }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setNote(""); }, [open]);

  const isAccept = decision === "accepted";
  const submit = async () => {
    setSaving(true);
    try {
      await onConfirm(note);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-none"
        data-testid={`decision-dialog-${decision}`}
      >
        <DialogHeader>
          <DialogTitle>
            {isAccept ? "Accept this offer?" : "Reject this offer?"}
          </DialogTitle>
          <DialogDescription>
            {offer
              ? `${offer.buyer_name || "Buyer"} — ${formatAmount(
                  offer.amount,
                  offer.currency || "£",
                )}`
              : ""}
            . The buyer will be emailed with your decision
            {isAccept ? " and your note below." : " and any note you leave below."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="decision-note" className="text-xs">
            Note to buyer (optional)
          </Label>
          <Textarea
            id="decision-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-none mt-1 min-h-[100px]"
            placeholder={
              isAccept
                ? "e.g. Delighted. My solicitor will be in touch this week."
                : "e.g. We're proceeding with a higher offer, thanks for your interest."
            }
            data-testid="decision-note-input"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="rounded-none"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={isAccept ? "default" : "destructive"}
            className="rounded-none"
            onClick={submit}
            disabled={saving}
            data-testid={`decision-confirm-${decision}`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isAccept ? (
              <><Check className="h-4 w-4 mr-2" /> Accept &amp; notify</>
            ) : (
              <><X className="h-4 w-4 mr-2" /> Reject &amp; notify</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({ open, onOpenChange, offer, onConfirm }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setNote(""); }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await onConfirm(note);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };
  const prev = offer?.status === "accepted" ? "accepted" : "rejected";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-none"
        data-testid="revoke-dialog"
      >
        <DialogHeader>
          <DialogTitle>Revoke your decision?</DialogTitle>
          <DialogDescription>
            {offer
              ? `You previously ${prev} ${offer.buyer_name || "the buyer"}'s offer of ${formatAmount(
                  offer.amount,
                  offer.currency || "£",
                )}. The offer will be re-opened and the buyer will be emailed.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="revoke-note" className="text-xs">
            Note to buyer (optional)
          </Label>
          <Textarea
            id="revoke-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-none mt-1 min-h-[100px]"
            placeholder="e.g. Reconsidered — happy to re-open discussions."
            data-testid="revoke-note-input"
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="rounded-none"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-none"
            onClick={submit}
            disabled={saving}
            data-testid="revoke-confirm-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <><Undo2 className="h-4 w-4 mr-2" /> Revoke &amp; notify</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PropertyAdminOffersReceivedTab({
  focusPropertyId = null,
  onClearFocus = null,
  onDataChange = null,
}) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("open"); // open | all
  const [dialog, setDialog] = useState(null); // { offer, decision } | { offer, action: 'revoke' }
  const [whatsappTarget, setWhatsappTarget] = useState(null); // offer for the WhatsApp modal
  const groupRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/property-admin/offers/received`,
        { withCredentials: true }
      );
      setOffers(data?.offers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-link support: when My Properties passes focusPropertyId, ensure the
  // filter includes that property (switch to All if the only offer is a
  // decided one) and scroll it into view.
  useEffect(() => {
    if (!focusPropertyId || loading) return;
    const propOffers = offers.filter((o) => o.property_id === focusPropertyId);
    const hasOpen = propOffers.some((o) => (o.status || "sent") === "sent");
    if (propOffers.length > 0 && !hasOpen && filter === "open") {
      setFilter("all");
    }
    // Scroll after a paint so the target exists.
    const t = setTimeout(() => {
      const el = groupRefs.current[focusPropertyId];
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-primary");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1800);
      }
      onClearFocus && onClearFocus();
    }, 120);
    return () => clearTimeout(t);
  }, [focusPropertyId, loading, offers, filter, onClearFocus]);

  const decide = async (offer, decision) => {
    // Now opens the note dialog instead of firing immediately.
    setDialog({ offer, decision });
  };

  const revoke = (offer) => {
    setDialog({ offer, action: "revoke" });
  };

  const confirmRevoke = async (note) => {
    if (!dialog?.offer) return;
    const { offer } = dialog;
    setBusy(`${offer.offer_id}:revoke`);
    try {
      await axios.post(
        `${API}/property-admin/offers/${offer.offer_id}/revoke`,
        { note },
        { withCredentials: true }
      );
      toast.success("Decision revoked — buyer notified");
      await load();
      onDataChange && onDataChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke decision");
    } finally {
      setBusy(null);
    }
  };

  const confirmDecision = async (note) => {
    if (!dialog) return;
    const { offer, decision } = dialog;
    setBusy(`${offer.offer_id}:${decision}`);
    try {
      await axios.post(
        `${API}/property-admin/offers/${offer.offer_id}/decision`,
        { status: decision, note },
        { withCredentials: true }
      );
      toast.success(
        decision === "accepted"
          ? "Offer accepted — buyer notified"
          : "Offer rejected — buyer notified"
      );
      await load();
      onDataChange && onDataChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update offer");
    } finally {
      setBusy(null);
    }
  };

  const visibleOffers = useMemo(() => {
    if (filter === "open") return offers.filter((o) => (o.status || "sent") === "sent");
    return offers;
  }, [offers, filter]);

  const propertyMap = useMemo(() => {
    const m = new Map();
    for (const o of visibleOffers) {
      if (!m.has(o.property_id)) {
        m.set(o.property_id, {
          property: o.property || { property_id: o.property_id },
          offers: [],
        });
      }
      m.get(o.property_id).offers.push(o);
    }
    return Array.from(m.values());
  }, [visibleOffers]);

  const openCount = offers.filter((o) => (o.status || "sent") === "sent").length;

  return (
    <div
      className="bg-white border border-border p-6"
      data-testid="offers-received-tab-content"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Inbox className="h-5 w-5" /> Offers received
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Offers buyers have made on your listings. Accept or reject to
            notify them by email.
          </p>
        </div>
        <div className="flex items-center gap-1 border border-border rounded-none p-0.5 text-xs">
          <button
            onClick={() => setFilter("open")}
            className={`px-3 py-1 ${
              filter === "open" ? "bg-primary text-primary-foreground" : ""
            }`}
            data-testid="offers-received-filter-open"
          >
            Open ({openCount})
          </button>
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1 ${
              filter === "all" ? "bg-primary text-primary-foreground" : ""
            }`}
            data-testid="offers-received-filter-all"
          >
            All ({offers.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : propertyMap.length === 0 ? (
        <div
          className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20 mt-6"
          data-testid="offers-received-empty"
        >
          <Inbox className="h-8 w-8 mx-auto mb-2 text-muted-foreground/60" />
          {filter === "open"
            ? "No open offers right now."
            : "You haven't received any offers yet."}
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 mt-6" data-testid="offers-received-list">
          {propertyMap.map(({ property: p, offers: propOffers }) => {
            const img = (p.gallery || [])[0]?.media_url || "";
            return (
              <li
                key={p.property_id}
                className="border border-border transition-shadow"
                ref={(el) => { if (el) groupRefs.current[p.property_id] = el; }}
                data-testid={`offers-received-property-${p.property_id}`}
              >
                <div className="flex gap-4 p-4">
                  <Link
                    to={`/property/${p.property_id}`}
                    className="w-24 h-24 bg-muted flex-shrink-0 overflow-hidden bg-cover bg-center"
                    style={img ? { backgroundImage: `url(${img})` } : {}}
                    aria-label={p.title}
                  />
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/property/${p.property_id}`}
                      className="font-serif text-base font-medium hover:underline block truncate"
                    >
                      {p.title || "Property"}
                    </Link>
                    {p.display_address && (
                      <div className="text-xs text-muted-foreground truncate">
                        {p.display_address}
                      </div>
                    )}
                    <div className="text-sm mt-1">
                      Listed at{" "}
                      <span className="font-semibold">
                        {formatAmount(p.price, p.currency || "£")}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="bg-muted/20 border-t border-border" data-testid={`offers-received-history-${p.property_id}`}>
                  <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                    Offers ({propOffers.length})
                  </div>
                  {propOffers.map((o) => (
                    <OfferRow
                      key={o.offer_id}
                      offer={o}
                      onDecide={decide}
                      onRevoke={revoke}
                      onMessageBuyer={(offer) => setWhatsappTarget({ offer, propertyTitle: p.title })}
                      busy={busy}
                    />
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <DecisionDialog
        open={!!dialog && dialog.action !== "revoke"}
        onOpenChange={(o) => { if (!o) setDialog(null); }}
        offer={dialog?.offer}
        decision={dialog?.decision || "accepted"}
        onConfirm={confirmDecision}
      />
      <RevokeDialog
        open={!!dialog && dialog.action === "revoke"}
        onOpenChange={(o) => { if (!o) setDialog(null); }}
        offer={dialog?.offer}
        onConfirm={confirmRevoke}
      />
      <WhatsAppUpdateModal
        open={!!whatsappTarget}
        onOpenChange={(o) => { if (!o) setWhatsappTarget(null); }}
        propertyId={whatsappTarget?.offer?.property_id}
        recipientNumber={whatsappTarget?.offer?.buyer_phone}
        recipientName={whatsappTarget?.offer?.buyer_name}
        defaultMessage={
          whatsappTarget
            ? `Hi ${whatsappTarget.offer.buyer_name || "there"}, just following up on your offer for ${whatsappTarget.propertyTitle || "the property"}.`
            : ""
        }
      />
    </div>
  );
}
